/* Sanki — Symmetric card image picker (Jun 2026)
   ----------------------------------------------------------
   Problem: Shopify featured_image is whatever image #1 the merchant
   uploaded. Filenames are random UUIDs, alt text is empty. We can't
   use server-side keyword heuristics. So we score images by visual
   features client-side and swap the card image to the highest-scoring
   "laydown" / "flat-shot" image.

   Scoring heuristic:
     • Draw image to 60×60 canvas.
     • Sample 4 corner regions (8×8 each).
     • Compute mean RGB of each corner.
     • LAYDOWN = all corners are bright + uniform (within RGB distance 35).
     • Penalty for warm/skintone-ish corners (faces near edges).

   Performance:
     • IntersectionObserver — only score cards that scroll into view.
     • Caches results in sessionStorage by product handle.
     • Limits concurrent canvases to 3 to keep the UI snappy.
*/
(function () {
  if (window.__sk_cardImagePicker) return;
  window.__sk_cardImagePicker = true;

  var CACHE_KEY = 'sk_card_imgpick_v1';
  var cache = {};
  try { cache = JSON.parse(sessionStorage.getItem(CACHE_KEY) || '{}'); } catch (e) {}

  function saveCache() {
    try { sessionStorage.setItem(CACHE_KEY, JSON.stringify(cache)); } catch (e) {}
  }

  // Pull image URL list from Shopify products.json (cached per collection)
  var productsByHandle = null;
  var productsPromise = null;

  function loadProducts() {
    if (productsByHandle) return Promise.resolve(productsByHandle);
    if (productsPromise) return productsPromise;
    var handleMatch = location.pathname.match(/\/collections\/([^\/]+)/);
    var url = handleMatch
      ? '/collections/' + handleMatch[1] + '/products.json?limit=250'
      : '/products.json?limit=250';
    productsPromise = fetch(url)
      .then(function (r) { return r.json(); })
      .then(function (d) {
        productsByHandle = {};
        (d.products || []).forEach(function (p) {
          productsByHandle[p.handle] = (p.images || []).map(function (i) { return i.src; });
        });
        return productsByHandle;
      })
      .catch(function () { return {}; });
    return productsPromise;
  }

  function scoreImage(url) {
    return new Promise(function (resolve) {
      var img = new Image();
      img.crossOrigin = 'anonymous';
      img.referrerPolicy = 'no-referrer';
      // Append size param so we fetch a tiny thumbnail
      var u = url;
      if (u.indexOf('?') === -1) u += '?width=160';
      else u += '&width=160';
      img.onload = function () {
        try {
          var canvas = document.createElement('canvas');
          canvas.width = 60; canvas.height = 60;
          var ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, 60, 60);
          var d = ctx.getImageData(0, 0, 60, 60).data;

          // Corner regions (top-left, top-right, bottom-left, bottom-right) at 8×8
          function corner(x0, y0) {
            var r = 0, g = 0, b = 0, n = 0;
            for (var y = y0; y < y0 + 8; y++) {
              for (var x = x0; x < x0 + 8; x++) {
                var i = (y * 60 + x) * 4;
                r += d[i]; g += d[i + 1]; b += d[i + 2]; n++;
              }
            }
            return [r / n, g / n, b / n];
          }
          var c1 = corner(0, 0);
          var c2 = corner(52, 0);
          var c3 = corner(0, 52);
          var c4 = corner(52, 52);
          var corners = [c1, c2, c3, c4];

          // Brightness — laydowns have bright corners (>200) typically
          var avgBright = corners.reduce(function (s, c) { return s + (c[0] + c[1] + c[2]) / 3; }, 0) / 4;

          // Uniformity — pairwise RGB distance
          var maxDist = 0;
          for (var i = 0; i < 4; i++) {
            for (var j = i + 1; j < 4; j++) {
              var dr = corners[i][0] - corners[j][0];
              var dg = corners[i][1] - corners[j][1];
              var db = corners[i][2] - corners[j][2];
              var dist = Math.sqrt(dr * dr + dg * dg + db * db);
              if (dist > maxDist) maxDist = dist;
            }
          }

          // Saturation penalty — colorful corners suggest a busy scene
          var avgSat = corners.reduce(function (s, c) {
            var mx = Math.max(c[0], c[1], c[2]);
            var mn = Math.min(c[0], c[1], c[2]);
            return s + (mx - mn);
          }, 0) / 4;

          // Final score: high brightness + low maxDist + low saturation = laydown
          // Range roughly: 0 (model shot) to 100 (pristine laydown)
          var brightScore = Math.max(0, (avgBright - 100) / 1.55); // 100→0, 255→100
          var uniformScore = Math.max(0, 100 - maxDist * 1.4); // 0→100, 70→0
          var satScore = Math.max(0, 100 - avgSat * 1.5);
          var score = (brightScore * 0.4) + (uniformScore * 0.4) + (satScore * 0.2);
          resolve(score);
        } catch (e) {
          resolve(0);
        }
      };
      img.onerror = function () { resolve(0); };
      img.src = u;
    });
  }

  function pickBest(images) {
    return Promise.all(images.slice(0, 6).map(scoreImage)).then(function (scores) {
      var best = 0, bestIdx = 0;
      scores.forEach(function (s, i) {
        if (s > best) { best = s; bestIdx = i; }
      });
      // Only swap if the best score is meaningfully better than image #0
      var firstScore = scores[0] || 0;
      if (best > firstScore + 8 && best > 40) {
        return { idx: bestIdx, score: best, scores: scores };
      }
      return null;
    });
  }

  function processCard(card, productsMap) {
    // Find product handle from card link
    var link = card.querySelector('a[href*="/products/"]');
    if (!link) return;
    var m = link.getAttribute('href').match(/\/products\/([^\/?#]+)/);
    if (!m) return;
    var handle = m[1];

    var imgEl = card.querySelector('.sk-card__img, .sk-bsg__img:not(.sk-bsg__img--hover), .sk-bsg__img:first-of-type');
    if (!imgEl || imgEl.tagName !== 'IMG') return;

    // Use cache if present
    if (cache[handle] != null) {
      if (cache[handle] !== -1) applySwap(imgEl, productsMap[handle], cache[handle]);
      return;
    }

    var images = productsMap[handle];
    if (!images || images.length < 2) {
      cache[handle] = -1; saveCache(); return;
    }

    pickBest(images).then(function (res) {
      if (!res) {
        cache[handle] = -1;
      } else {
        cache[handle] = res.idx;
        applySwap(imgEl, images, res.idx);
      }
      saveCache();
    });
  }

  function applySwap(imgEl, images, idx) {
    if (!images || !images[idx]) return;
    var newSrc = images[idx];
    // Build size variants via Shopify image URL transformation
    function sized(w) {
      if (newSrc.indexOf('?') !== -1) return newSrc + '&width=' + w;
      return newSrc + '?width=' + w;
    }
    // Fade-swap for smoothness
    var clone = new Image();
    clone.onload = function () {
      imgEl.style.transition = 'opacity 240ms ease';
      imgEl.style.opacity = '0';
      setTimeout(function () {
        imgEl.src = sized(720);
        imgEl.srcset = sized(360) + ' 360w, ' + sized(540) + ' 540w, ' + sized(720) + ' 720w, ' + sized(1080) + ' 1080w';
        imgEl.style.opacity = '1';
      }, 120);
    };
    clone.src = sized(720);
  }

  function init() {
    var cards = document.querySelectorAll('.sk-card, .sk-bsg__card');
    if (!cards.length) return;

    loadProducts().then(function (productsMap) {
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            io.unobserve(entry.target);
            requestIdleCallback
              ? requestIdleCallback(function () { processCard(entry.target, productsMap); })
              : setTimeout(function () { processCard(entry.target, productsMap); }, 0);
          }
        });
      }, { rootMargin: '200px' });

      cards.forEach(function (c) { io.observe(c); });
    });
  }

  if (document.readyState === 'complete' || document.readyState === 'interactive') {
    init();
  } else {
    document.addEventListener('DOMContentLoaded', init);
  }
})();
